/**
 * EWBCI ユーティリティカスタムフック
 */

import { useCallback } from 'react';

import Log from '@/Logger';

import type { EWBCIHooks } from './useEWBCI';
import type { ItpcmdRequestBody } from '../core/client';
import type { BlobURL, EWBCIItpcmdResponse } from '../core/types';
import type { ItpcmdPatchFormPageResponse } from '../utils/itpcmd';
import { EWBCIError, EWBCIErrorType } from '../utils/errors';

export type EWBCIUtilsHooks = {
    patchPageForm: (
        requestId: string,
        pageId: string,
        fields: Record<string, { prev: string; next: string; }>
    ) => Promise<EWBCIItpcmdResponse<ItpcmdPatchFormPageResponse>>;
    getDocumentFile: (docid: string, filename: string) => Promise<Blob>;
    getLatestDocumentPDF: (docid: string) => Promise<BlobURL>;
};

/**
 * EWBCI ユーティリティフック
 * 
 * - 文書関連のファイル取得
 * - 最新の文書関連のPDF取得
 * - その他ユーティリティ関数
 * 
 * @param hooks useEWBCIの戻り値
 * @returns ユーティリティ関数群
 */
export function useEWBCIUtils(hooks: EWBCIHooks): EWBCIUtilsHooks {
    // クライアントインスタンス
    const { _client: clientRef } = hooks;

    const patchPageForm = useCallback(async (requestId: string, pageId: string, fields: Record<string, { prev: string, next: string }>): Promise<EWBCIItpcmdResponse<ItpcmdPatchFormPageResponse>> => {
        const logger = Log.getLogger();
        if (!clientRef.current) {
            throw new EWBCIError('Client not initialized', EWBCIErrorType.AssertError);
        }

        try {
            logger.ui.debug('useEWBCIUtils', 'patchPageForm', `Sending request...`);

            const itpcmd: ItpcmdRequestBody = {
                method: "PATCH",
                query: `form/${pageId}`,
                body: JSON.stringify({
                    // 条件を満たしたものだけは更新する
                    strict: false,
                    // 対象のフィールドのみ最新の値を取得する
                    fetch: false,
                    // 読み取り専用フィールドの更新は行わない
                    force: false,
                    // 前提値
                    expected: Object.entries(fields).reduce((expected, [key, { prev: value }]) => ({ ...expected, [key]: value }), {}),
                    // 更新値
                    form: Object.entries(fields).reduce((form, [key, { next: value }]) => ({ ...form, [key]: value }), {}),
                }),
            };

            // リクエストを実行
            const result = await clientRef.current.executeItpcmd(itpcmd, requestId /* , timeoutMs */);
            logger.ui.debug('useEWBCIUtils', 'patchPageForm', 'Received response.');

            return result as EWBCIItpcmdResponse<ItpcmdPatchFormPageResponse>;

        } catch (error) {
            if (error instanceof EWBCIError) throw error;
            const ewbciError = new EWBCIError('Failed to patch page\'s form ', EWBCIErrorType.Unknown, error);
            logger.ui.error('useEWBCIUtils', 'patchPageForm', ewbciError.getDetailedMessage());
            throw ewbciError;
        }
    }, [clientRef]);

    /**
     * 文書関連のファイルを取得する
     * 
     * @param docid 文書ID
     * @param filename ファイル名
     * @return Blob 取得したファイルのBlob
     * @throws EWBCIError 取得に失敗した場合
     */
    const getDocumentFile = useCallback(async (docid: string, filename: string): Promise<Blob> => {
        const logger = Log.getLogger();
        if (!clientRef.current) {
            throw new EWBCIError('Client not initialized', EWBCIErrorType.AssertError);
        }

        try {
            logger.ui.debug('useEWBCIUtils', 'getDocumentFile', `Sending request...`);
            const result = await clientRef.current.fetchFileInDocument(docid, filename);
            logger.ui.debug('useEWBCIUtils', 'getDocumentFile', 'Received response.');

            return result;

        } catch (error) {
            const ewbciError = new EWBCIError('Failed to get document file', EWBCIErrorType.Unknown, error);
            logger.ui.error('useEWBCIUtils', 'getDocumentFile', ewbciError.getDetailedMessage());
            throw ewbciError;
        }
    }, [clientRef]);

    /**
     * 最新の文書関連のPDFファイルを取得する
     * 
     * @param docid 文書ID
     * @return BlobURL 取得したPDFファイルのBlobURL ※呼び出し元でURL.revokeObjectURL()で解放すること
     * @throws EWBCIError 取得に失敗した場合
     */
    const getLatestDocumentPDF = useCallback(async (docid: string): Promise<BlobURL> => {
        const logger = Log.getLogger();
        if (!clientRef.current) {
            throw new EWBCIError('EWBCI client is not initialized', EWBCIErrorType.AssertError);
        }
        try {
            logger.ui.debug('useEWBCIUtils', 'getLatestDocumentPDF', `Fetching document PDF: docid=${docid}`);
            const blob = await clientRef.current.getLatestDocumentPDF(docid);
            logger.ui.info('useEWBCIUtils', 'getLatestDocumentPDF', `Successfully fetched document PDF: docid=${docid}`);

            const blobUrl = URL.createObjectURL(blob);
            return blobUrl;

        } catch (error) {
            const ewbciError = new EWBCIError('Failed to fetch document PDF', EWBCIErrorType.Unknown, error);
            logger.ui.error('useEWBCIUtils', 'getLatestDocumentPDF', ewbciError.getDetailedMessage());
            throw ewbciError;
        }
    }, [clientRef]);

    return {
        patchPageForm,
        getDocumentFile,
        getLatestDocumentPDF,
    };
}
